/** Local interpretation consumes the entire transmission; unsupported fragments never disappear. */
import { COMMAND_CATALOG, validateTransmission, type PilotTransmission, type PilotMessage, type Instruction, type QuerySubject } from '@atc/sim';
import { resolveCallsign, resolveHeardClearance, resolveSpokenCallsign, type ResolveContext } from './voice-resolve';

export type AddressResolution = { status: 'resolved' | 'ambiguous' | 'unknown'; heardCallsign: string; resolvedCallsign?: string; candidates: string[]; reason: string };
export type Interpretation = { ok: true; transmission: PilotTransmission; address?: AddressResolution } | { ok: false; callsign?: string; reason: string; address?: AddressResolution };
const digits: Record<string, string> = { zero: '0', oh: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', niner: '9' };
export function normalizeNumbers(s: string): string {
  return s.toLowerCase().replace(/\b(?:zero|oh|one|two|three|four|five|six|seven|eight|nine|niner)(?:[ -]+(?:zero|oh|one|two|three|four|five|six|seven|eight|nine|niner))*\b/g, (m) => m.split(/[ -]+/).map((w) => digits[w]).join(''))
    .replace(/\b(\d{1,2}) thousand(?: (\d) hundred)?\b/g, (_, n, h) => String(Number(n) * 1000 + Number(h ?? 0) * 100))
    .replace(/\b(\d{1,2}) (left|right|center|centre)\b/g, (_, n, side) => n + side[0]);
}
/** A transmission that starts here carries no callsign of its own: the body begins with the call. */
const CALL_START = /^(?:turn|fly|heading|climb|descend|reduce|increase|maintain|speed|proceed|direct|hold|cleared|line up|go around|abort|resume|contact|say|what|are you|stand by|radar contact|roger|negative|correction)\b/i;
/** The most words in front of the call that may be written off as a callsign the transcript lost. */
const LOST_ADDRESS_WORDS = 4;

export function addressText(text: string, ctx: ResolveContext, selected?: string): { callsign?: string; body: string; assumed?: boolean } {
  // "4,000" is one number; other commas and punctuation only separate words.
  const clean = text.trim().replace(/(\d),(?=\d{3}\b)/g, '$1');
  const words = clean.replace(/[,?!;.]/g, ' ').split(/\s+/);
  for (let n = Math.min(10, words.length - 1); n > 0; n--) {
    const cs = resolveCallsign(words.slice(0, n).join(' '), ctx);
    if (cs) return { callsign: cs, body: words.slice(n).join(' ') };
  }
  // Selection assistance only applies when the opening phrase is clearly a command/query.
  if (selected && CALL_START.test(clean)) return { callsign: selected, body: clean };
  // Or the callsign was spoken and the transcript mangled it past matching. An aircraft picked on
  // the radar stands in for it, but only for the few words in front of a call that is otherwise
  // plain: anything longer is not a lost callsign, it is a transmission nobody understood.
  if (ctx.selected) {
    for (let n = 1; n <= Math.min(LOST_ADDRESS_WORDS, words.length - 1); n++) {
      const body = words.slice(n).join(' ');
      if (CALL_START.test(body)) return { callsign: ctx.selected, body, assumed: true };
    }
  }
  return { body: clean };
}

export function interpretLocal(text: string, ctx: ResolveContext, selected?: string): Interpretation {
  const { callsign, body } = addressText(text, ctx, selected);
  if (!callsign) return { ok: false, reason: 'Say the full callsign.' };
  const source = normalizeNumbers(body).replace(/^(?:negative|correction)\s*/, '').replace(/[?!.,;]+/g, ' ').replace(/\s+/g, ' ').trim();
  // Commas are gone by now, so a new clause starts at a leading command verb, with or without "and"/"then".
  // "are you cleared to land" stays one question.
  const clauses = source.split(/ (?:(?:then|and) )?(?<!\byou )(?<!\bradar )(?=(?:turn|climb|descend|reduce|increase|say|what|go around|cleared|hold|proceed|resume|contact|fly heading|stand by|radar contact|roger)\b)/).filter(Boolean);
  const messages: PilotMessage[] = [];
  for (const clause of clauses) {
    let matched = false;
    const q = /^(?:say|report|what (?:is|are)(?: your)?)(?: current)? (instructions?|clearance|heading|altitude|speed|intentions|assigned heading|assigned altitude|assigned speed)$/.exec(clause);
    if (q) {
      const subject = ['clearance', 'instruction'].includes(q[1]!) ? 'instructions' : q[1]!.replace(/ ([a-z])/g, (_, c) => c.toUpperCase());
      messages.push({ kind: 'query', subject: subject as QuerySubject }); continue;
    }
    for (const entry of COMMAND_CATALOG) {
      const m = new RegExp(`^(?:${entry.pattern})$`, 'i').exec(clause);
      if (!m) continue;
      matched = true;
      if (entry.category === 'Questions') { messages.push({ kind: 'query', subject: entry.subject as QuerySubject }); break; }
      if (entry.id === 'standby' || entry.id === 'repeat' || entry.id === 'acknowledge') { messages.push({ kind: entry.id }); break; }
      if (entry.id === 'contactTower') { messages.push({ kind: 'towerTransfer' }); break; }
      let instruction: unknown;
      switch (entry.id) {
        case 'heading': instruction = { kind: 'heading', headingDeg: Number(m[2]), ...(m[1] ? { turn: m[1] === 'left' ? 'L' : 'R' } : {}) }; break;
        case 'altitude': instruction = { kind: 'altitude', altitudeFt: Number(m[1]), expedite: !!m[2] }; break;
        case 'speed': instruction = { kind: 'speed', speedKt: Number(m[1]) }; break;
        case 'direct': case 'hold': instruction = { kind: entry.id, fix: m[1] }; break;
        case 'land': case 'approach': instruction = { kind: entry.id, runway: m[1]!.toUpperCase() }; break;
        default: instruction = { kind: entry.id };
      }
      const resolved = resolveHeardClearance({ spokenCallsign: callsign, instructions: [instruction] }, ctx);
      if (!resolved.ok) return { ok: false, callsign, reason: resolved.reason };
      messages.push({ kind: 'instruction', instruction: resolved.clearance.instructions[0] as Instruction }); break;
    }
    if (!matched) return { ok: false, callsign, reason: /^(?:reduce|increase)(?: speed)?$/.test(clause) ? 'Say speed in knots.' : 'Say again, include the instruction and all values.' };
  }
  const tx = validateTransmission({ callsign, messages });
  return tx ? { ok: true, transmission: tx } : { ok: false, callsign, reason: 'Say again.' };
}

/** Re-resolve untrusted model names and validate every message before any simulation change. */
export function resolveModelTransmission(raw: unknown, ctx: ResolveContext): Interpretation {
  if (!raw || typeof raw !== 'object') return { ok: false, reason: 'Say again.' };
  const r = raw as { callsign?: unknown; messages?: unknown; address?: unknown };
  if (r.address && typeof r.address === 'object') {
    const a = r.address as Record<string, unknown>;
    const status = a.status;
    const heardCallsign = typeof a.heardCallsign === 'string' ? a.heardCallsign : '';
    const candidates = Array.isArray(a.candidates) ? a.candidates.filter((x): x is string => typeof x === 'string' && !!ctx.aircraft.find((ac) => ac.callsign === x)).slice(0, 5) : [];
    const reason = typeof a.reason === 'string' ? a.reason : '';
    if (!['resolved', 'ambiguous', 'unknown'].includes(String(status))) return { ok: false, reason: 'Say again, invalid addressing result.' };
    const address: AddressResolution = { status: status as AddressResolution['status'], heardCallsign, candidates, reason };
    if (status !== 'resolved') return { ok: false, reason: status === 'ambiguous' ? 'Aircraft calling, say again callsign.' : 'Say the full callsign.', address };
    const proposed = typeof a.resolvedCallsign === 'string' ? a.resolvedCallsign : '';
    // The selected aircraft may stand in for a callsign that matched nothing, but the service's
    // own answer still has to agree with it: a model naming some other aircraft is never accepted.
    const { callsign: verified } = resolveSpokenCallsign(heardCallsign, ctx);
    if (!verified || verified !== proposed) return { ok: false, reason: 'Aircraft calling, say again callsign.', address: { ...address, status: verified ? 'ambiguous' : 'unknown', ...(verified ? { candidates: [verified, ...candidates.filter((x) => x !== verified)] } : {}) } };
    address.resolvedCallsign = verified;
    const tx = validateTransmission({ callsign: verified, messages: r.messages });
    if (!tx) return { ok: false, callsign: verified, reason: 'Say again, unsupported transmission.', address };
    return { ok: true, transmission: tx, address };
  }
  // Compatibility with the original endpoint and saved test fixtures.
  const callsign = typeof r.callsign === 'string' ? resolveCallsign(r.callsign, ctx) : null;
  const tx = callsign ? validateTransmission({ ...r, callsign }) : null;
  if (!tx) return { ok: false, reason: 'Say again, unresolved aircraft or instruction.' };
  return { ok: true, transmission: tx };
}
